import React, { FC } from 'react';
import Props from './typing';
import './styles.scss';
import { StepStatus } from '../SideBar/sidebar.constant';
import { Tooltip } from 'antd';

const CustomStepper: FC<Props> = (props) => {
  const { currentStep, stepperData, numberOfSteps, stepStatuses, onStepClick } = props;

  const getStepStatus = (index: number): StepStatus | null => {
    return stepStatuses?.[index] ?? null;
  };

  const isClickable = (index: number): boolean => {
    if (!onStepClick) return false;
    const status = getStepStatus(index);
    return status !== null && status !== 'LOCKED' && status !== 'COMPLETED_LOCKED';
  };

  const isDisabled = (index: number): boolean => {
    if (!stepStatuses) return false;
    const status = getStepStatus(index);
    return status === null || status === 'LOCKED' || status === 'COMPLETED_LOCKED';
  };

  const handleClick = (index: number) => {
    if (isClickable(index)) {
      onStepClick?.(index);
    }
  };

  const getCircleClass = (index: number): string => {
    const status = getStepStatus(index);
    const isActive = currentStep === index;

    if (status === 'COMPLETED' || status === 'COMPLETED_LOCKED') return 'stepper-circle stepper-circle--completed';
    if (status === 'ERROR' || status === 'INVALIDATED') return 'stepper-circle stepper-circle--warning';
    if (status === 'LOCKED') return 'stepper-circle stepper-circle--locked';
    if (isActive) return 'stepper-circle stepper-circle--active';
    return 'stepper-circle stepper-circle--default';
  };

  const renderCircleContent = (index: number) => {
    const status = getStepStatus(index);
    if (status === 'COMPLETED' || status === 'COMPLETED_LOCKED') {
      return (
        <svg width="10" height="8" viewBox="0 0 12 10" fill="none">
          <path d="M1 5L4.5 8.5L11 1" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    }
    if (status === 'ERROR' || status === 'INVALIDATED') {
      return (
        <svg width="3" height="10" viewBox="0 0 4 14" fill="none">
          <line x1="2" y1="1" x2="2" y2="8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          <circle cx="2" cy="12" r="1.2" fill="#fff" />
        </svg>
      );
    }
    return null;
  };

  return (
    <div className="custom-stepper">
      {stepperData.map((value, index) => {
        const disabled = isDisabled(index);
        const clickable = isClickable(index);
        const isActive = currentStep === index;
        const isLast = index === numberOfSteps - 1;

        const status = getStepStatus(index);
        const isLocked = status === 'LOCKED' || status === 'COMPLETED_LOCKED';

        const rowContent = (
          <div className={`custom-stepper__row${isActive ? ' custom-stepper__row--active' : ''}`}>
            <div className={getCircleClass(index)}>
              {renderCircleContent(index)}
            </div>
            <span className={`custom-stepper__label${
              isActive ? ' custom-stepper__label--active' : ''
            }${disabled ? ' custom-stepper__label--disabled' : ''}`}>
              {value}
            </span>
          </div>
        );

        const tooltipTitle = isLocked
          ? 'This step is locked'
          : 'Complete previous steps to continue';

        return (
          <div
            className={`custom-stepper__step${
              disabled ? ' custom-stepper__step--disabled' : ''
            }${clickable ? ' custom-stepper__step--clickable' : ''}`}
            key={value}
            onClick={disabled ? undefined : () => handleClick(index)}
          >
            {disabled ? (
              <Tooltip 
                title={tooltipTitle}
                placement="right"
                mouseEnterDelay={1}
                align={{ offset: [-10, 0] }}
                overlayInnerStyle={{ borderRadius: '8px' }}
              >
                <div>{rowContent}</div>
              </Tooltip>
            ) : rowContent}
            {!isLast && (
              <div className={`custom-stepper__line${
                status === 'COMPLETED' || status === 'COMPLETED_LOCKED' ? ' custom-stepper__line--completed' : ''
              }`} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CustomStepper;
